import React, { useState } from 'react';
import { FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { MinimalDocument } from '../../types';
import { getDocumentIcon, getDocumentIconColor } from '../../utils/documentIcons';
import { useTheme } from '../../contexts/ThemeContext';
import { detectMentions } from '../../utils/mentionUtils';
import { formatPeriodForCitation } from '../../utils/formatPeriod';

interface MessageContextSectionProps {
  selectedDocuments?: string[];
  availableDocuments?: MinimalDocument[];
  messageText?: string; // Texto del mensaje para extraer nombres (misma lógica que chips)
  citations?: Record<
    string,
    {
      document_id?: string;
      document_name?: string;
      company_name?: string;
      document_type?: string;
      document_period?: string;
    }
  >; // Citations del mensaje para lookup de nombres por ID (fallback)
  isUserMessage?: boolean;
  onDocumentClick?: (documentId: string) => void;
  maxVisible?: number;
  className?: string;
  alwaysExpanded?: boolean; // Si es true, siempre muestra el contenido sin botón de collapse
}

/**
 * Componente para mostrar el contexto (documentos seleccionados) al final del mensaje.
 * Similar a UsageStats: colapsable por defecto, discreto, al final del mensaje.
 *
 * @param selectedDocuments - IDs de documentos históricos del mensaje
 * @param availableDocuments - Array de MinimalDocument para obtener metadata
 * @param messageText - Texto del mensaje para extraer nombres (misma lógica que chips)
 * @param citations - Citations del mensaje para lookup de nombres por ID (fallback)
 * @param isUserMessage - Si es mensaje de usuario (afecta estilos)
 * @param onDocumentClick - Callback cuando se hace click en un documento
 * @param maxVisible - Máximo de documentos visibles antes de mostrar "+N más"
 * @param className - Clases CSS adicionales
 */
const MessageContextSection: React.FC<MessageContextSectionProps> = ({
  selectedDocuments = [],
  availableDocuments = [],
  messageText = '',
  citations = {},
  isUserMessage = false,
  onDocumentClick,
  maxVisible = 3,
  className = '',
  alwaysExpanded = false, // Por defecto, tiene su propio estado de collapse
}) => {
  const { theme } = useTheme();

  // Si alwaysExpanded es true, siempre mostrar contenido (controlado desde fuera)
  // De lo contrario, mantener estado colapsado por defecto (igual que UsageStats)
  const [isCollapsed, setIsCollapsed] = useState(!alwaysExpanded);

  // Solo renderizar si hay documentos
  if (!selectedDocuments || selectedDocuments.length === 0) {
    return null;
  }

  // Lookup simple por ID: availableDocuments primero, messageText, luego citations
  const documentMetadata = React.useMemo(() => {
    if (!selectedDocuments?.length) return [];

    // 1. Buscar en availableDocuments por ID (lookup simple)
    const docById = new Map(availableDocuments?.map((d) => [d.id, d]) || []);

    // 2. Extraer nombres de documentos desde texto (misma lógica que chips de menciones)
    const namesFromText: string[] = [];
    if (messageText && isUserMessage) {
      const mentions = detectMentions(messageText);
      mentions.filter((m) => m.type === 'documento').forEach((m) => namesFromText.push(m.value));
    }

    // 3. Buscar en citations por document_id (fallback)
    const citationById = new Map<string, MinimalDocument>();
    if (citations) {
      Object.values(citations).forEach((c) => {
        if (c.document_id && !docById.has(c.document_id)) {
          const name =
            c.company_name && c.document_type
              ? `${c.company_name} - ${c.document_type}${formatPeriodForCitation(
                  c.document_period,
                  c.document_type
                )}`
              : c.document_name;
          if (name) {
            citationById.set(c.document_id, {
              id: c.document_id,
              name,
              documentType: c.document_type || 'pdf',
              scope: 'private' as const,
              original_filename: c.document_name || name,
            });
          }
        }
      });
    }

    // 4. Combinar en orden de prioridad: availableDocuments > messageText > citations
    return selectedDocuments
      .map((id, index) => {
        // Primero: availableDocuments (más confiable)
        if (docById.has(id)) {
          return docById.get(id)!;
        }
        // Segundo: nombre desde texto (por orden, mismo que chips)
        if (index < namesFromText.length) {
          return {
            id,
            name: namesFromText[index],
            documentType: 'pdf' as const,
            scope: 'private' as const,
            original_filename: namesFromText[index],
          } as MinimalDocument;
        }
        // Tercero: citations (fallback)
        return citationById.get(id);
      })
      .filter(Boolean) as MinimalDocument[];
  }, [selectedDocuments, availableDocuments, messageText, citations, isUserMessage]);

  const visibleDocuments = documentMetadata.slice(0, maxVisible);
  const remainingCount = documentMetadata.length - maxVisible;

  const handleDocumentClick = (documentId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDocumentClick) {
      onDocumentClick(documentId);
    }
  };

  // Si no hay documentos encontrados, no mostrar nada
  if (documentMetadata.length === 0) {
    return null;
  }

  // Si alwaysExpanded es true, no mostrar border-t ni mt-1.5 pt-1 ya que está dentro de otra sección
  const containerClasses = alwaysExpanded
    ? `space-y-1 ${className}`
    : `mt-1.5 pt-1 border-t ${
        isUserMessage ? 'border-white/10' : 'border-nb-border/30'
      } space-y-1 ${className}`;

  return (
    <div className={containerClasses}>
      {/* Indicador Mínimo - Visible solo cuando está colapsado (similar a UsageStats) */}
      {/* Si alwaysExpanded es true, nunca mostrar el botón colapsado */}
      {/* Toggle button - always visible when not alwaysExpanded */}
      {!alwaysExpanded && (
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className={`w-full flex items-center justify-between text-[10px] transition-colors group ${
            isUserMessage
              ? 'text-white/70 hover:text-white'
              : 'text-nb-text-secondary/70 hover:text-nb-text-secondary'
          }`}
        >
          <span className="opacity-60 group-hover:opacity-100">
            <FileText
              className={`w-3 h-3 inline mr-1 ${
                isUserMessage ? 'text-white/70' : 'text-nb-text-secondary'
              }`}
            />
            <span className={isUserMessage ? 'text-white/70' : 'text-nb-text-secondary'}>
              {documentMetadata.length} documento{documentMetadata.length !== 1 ? 's' : ''}
            </span>
          </span>
          <span
            className={`opacity-40 group-hover:opacity-60 text-[9px] transition-transform duration-200 ${
              isCollapsed ? '' : 'rotate-90'
            }`}
          >
            ⋯
          </span>
        </button>
      )}
      {/* F1: Animated expand/collapse content */}
      <div
        className="grid transition-[grid-template-rows] duration-200 ease-in-out"
        style={{ gridTemplateRows: !alwaysExpanded && isCollapsed ? '0fr' : '1fr' }}
      >
        <div className="overflow-hidden min-h-0">
          {/* Header Contexto - Expandido pero discreto */}
          <div className="flex items-center justify-between">
            <div className="text-[10px] flex items-center gap-x-2 opacity-70">
              <FileText
                className={`w-3 h-3 ${isUserMessage ? 'text-white/70' : 'text-nb-text-secondary'}`}
              />
              <span className={isUserMessage ? 'text-white/70' : 'text-nb-text-secondary'}>
                <span className={isUserMessage ? 'text-white/90' : 'text-nb-text'}>
                  {documentMetadata.length}
                </span>{' '}
                documento{documentMetadata.length !== 1 ? 's' : ''} en contexto
              </span>
            </div>
            <button
              onClick={() => setIsCollapsed(true)}
              className={`text-[9px] opacity-60 ${
                isUserMessage
                  ? 'text-white/50 hover:text-white/70'
                  : 'text-nb-text-secondary/50 hover:text-nb-text-secondary'
              }`}
              title="Ocultar contexto"
            >
              −
            </button>
          </div>

          {/* Documentos - Visible cuando está expandido */}
          <div
            className={`text-[9px] space-y-0.5 pl-2 border-l ${
              isUserMessage
                ? 'text-white/60 border-white/10'
                : 'text-nb-text-secondary/60 border-nb-border/20'
            }`}
          >
            <div className="flex flex-wrap items-center gap-2">
              {visibleDocuments.map((doc) => {
                const Icon = getDocumentIcon(doc.documentType || 'pdf');
                const iconColor = isUserMessage
                  ? 'text-white/80'
                  : getDocumentIconColor(doc.scope || 'private');

                return (
                  <button
                    key={doc.id}
                    type="button"
                    onClick={(e) => handleDocumentClick(doc.id, e)}
                    className={`
                      inline-flex items-center gap-1.5 px-2 py-1 rounded border text-xs transition-all duration-200
                      focus:outline-none focus:ring-2 focus:ring-offset-1
                      ${
                        isUserMessage
                          ? 'bg-white/5 border-white/10 text-white/90 hover:bg-white/15 hover:border-white/20 hover:text-white focus:ring-white/50'
                          : 'bg-nb-surface border-nb-border text-nb-text hover:bg-nb-surface-hover hover:border-nb-border focus:ring-nb-accent'
                      }
                    `}
                    title={doc.name}
                    aria-label={`Abrir documento: ${doc.name}`}
                  >
                    <Icon className={`w-3.5 h-3.5 ${iconColor} flex-shrink-0`} />
                    <span className="font-medium whitespace-normal break-words min-w-0">
                      {doc.name}
                    </span>
                  </button>
                );
              })}

              {/* Badge "+N más" si hay más documentos */}
              {remainingCount > 0 && (
                <span
                  className={`
                    inline-flex items-center px-2 py-1 rounded border text-xs font-medium
                    ${
                      isUserMessage
                        ? 'bg-white/5 border-white/10 text-white/70'
                        : 'bg-nb-surface border-nb-border text-nb-text-secondary'
                    }
                  `}
                  title={`${remainingCount} documento${remainingCount !== 1 ? 's' : ''} más`}
                  aria-label={`${remainingCount} documento${remainingCount !== 1 ? 's' : ''} más`}
                >
                  +{remainingCount} más
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(MessageContextSection);
